import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormValues, employeeModel } from '~/entities/Employee'
import { Region, Role, Subdivision } from '~/shared/api'
import {
  localeEnumOptions,
  Option,
  timezoneCodeOptions,
} from '~/shared/config/constants'
import { getObject } from '~/shared/lib/utils'
import {
  FormButtons,
  formButtonsModel,
  FormProvider,
  TextInput,
  AsyncAutocompleteInput,
  AutocompleteInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'

export type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
}: FormProps) {
  const { t } = useTranslation()
  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting

  const form = useForm<FormValues>({
    resolver: zodResolver(employeeModel.formSchema),
    defaultValues,
  })

  const { reset, setValue, watch } = form

  watch((_, { type, name }) => {
    if (type !== 'change') return

    if (name === 'regionsIds') {
      setValue('subdivisionsIds', [])
    }
  })

  const regionsOptions = watch('regionsIds') as Option[]
  const regionsIds = (regionsOptions || []).map(
    (regionOption) => regionOption?.id,
  )

  const selectedLocale = watch('locale')
  const selectedLocaleId = getObject(selectedLocale)?.id

  useEffect(() => {
    if (defaultValues) reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Container>
        <ContainerTitle>{t('form.information')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <TextInput
              label='form.full name'
              name='name'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={6}>
            <TextInput
              label='form.E-mail'
              name='email'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
        </Grid>
      </Container>

      <Container data-testid='container-regions'>
        <ContainerTitle>{t('form.regions')}</ContainerTitle>
        <Grid container spacing={2.5} alignItems='flex-end'>
          <Grid container xs={6}>
            <AsyncAutocompleteInput
              name='regionsIds'
              placeholder='form.add region'
              fetchOptions={Region.fetchOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
              multiple
              autocompleteBoxSx={{ padding: 0 }}
              autocompleteXs={12}
              dataTestId='Регионы'
            />
          </Grid>
          <Grid xs={3}>
            <AutocompleteInput
              name='timezone'
              label='form.timezone'
              placeholder='form.timezone'
              options={timezoneCodeOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={3}>
            <AutocompleteInput
              name='locale'
              label='form.localization'
              placeholder='form.select'
              options={localeEnumOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
              startDecorator={
                selectedLocaleId ? (
                  <img
                    src={`/icons/${selectedLocaleId}.png`}
                    width='26px'
                    alt='icon flag'
                  />
                ) : null
              }
            />
          </Grid>
        </Grid>
      </Container>

      <Container data-testid='container-subdivisions'>
        <ContainerTitle>{t('form.subdivisions')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <AsyncAutocompleteInput
            name='subdivisionsIds'
            placeholder='form.add subdivision'
            fetchOptions={(search: string) =>
              Subdivision.fetchOptions(search, regionsIds.map(String))
            }
            queryKey={regionsIds}
            skeletonShow={isLoading}
            readOnly={!regionsOptions?.length || disabled}
            multiple
            autocompleteXs={6}
            dataTestId='Подразделение'
          />
        </Grid>
      </Container>

      <Container data-testid='container-roles'>
        <ContainerTitle>{t('form.roles')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <AsyncAutocompleteInput
            name='rolesIds'
            placeholder='form.add role'
            fetchOptions={Role.fetchOptions}
            skeletonShow={isLoading}
            readOnly={disabled}
            multiple
            autocompleteXs={6}
            dataTestId='Роли'
          />
        </Grid>
      </Container>

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
